import React from 'react';
import { FormikValues } from 'formik';
import { intersectionWith, isEmpty, includes } from 'lodash';

import {
  Content,
  ContentApproval,
  ContentCommentary,
  Group,
  GroupIdApproval,
  UserProfile,
  UserType,
  S3Object,
  UrlMetadataImage,
  S3VideoObject,
  ContentFragment,
  ContentDisclosures,
} from 'api';
import { USER_FEED_ID } from 'constants/index';
import { queryString } from 'utilities';

import { Manual, Url } from './layouts';
import { Facebook, Instagram, Reddit, Twitter, YouTube } from './partials';
import { EmbedFacebook, EmbedInstagram, EmbedReddit, EmbedTwitter, EmbedYouTube } from './widgets';

type ContentType = Content | ContentFragment | FormikValues;
type DisplayImage = S3Object | UrlMetadataImage | string;

const { REACT_APP_IMGIX_HOST } = process.env;

const HOST_MATCHERS = {
  facebook: 'www.facebook.com',
  instagram: 'www.instagram.com',
  linkedin: 'www.linkedin.com',
  reddit: 'www.reddit.com',
  twitter: 'twitter.com',
  youtube: 'www.youtube.com',
  youtubeShortLink: 'youtu.be',
};

const isHostMatch = (content: ContentType, match: string) => {
  try {
    const { content_object } = content;

    if (!content_object.url) {
      return false;
    }

    const objectURL = new URL(content_object.url);
    return objectURL.hostname === match;
  } catch (e) {
    return false;
  }
};

export const getLayout = (content: ContentType) => {
  if (isFacebook(content)) {
    return Facebook;
  }

  if (isInstagram(content)) {
    return Instagram;
  }

  if (isReddit(content)) {
    return Reddit;
  }

  if (isTweet(content)) {
    return Twitter;
  }

  if (isYouTube(content)) {
    return YouTube;
  }

  if (isLinkedIn(content)) {
    return Url;
  }

  if (hasVideo(content)) {
    return Manual;
  }

  if (isUrl(content)) {
    return Url;
  }

  return Manual;
};

export const getEmbed = (content: ContentType) => {
  if (isFacebook(content)) {
    const url = getUrl(content);
    return <EmbedFacebook url={url} />;
  }

  if (isInstagram(content)) {
    const shortcode = getInstagramShortcode(content);
    return <EmbedInstagram shortcode={shortcode} />;
  }

  if (isReddit(content)) {
    const url = getUrl(content);
    return <EmbedReddit url={url} />;
  }

  if (isYouTube(content)) {
    const id = getYouTubeId(content);
    return <EmbedYouTube id={id} />;
  }

  if (isTwitter(content)) {
    const id = getTweetId(content);
    return <EmbedTwitter id={id} />;
  }

  return null;
};

export const isEmbed = (content: ContentType) => {
  return (
    isFacebook(content) ||
    isInstagram(content) ||
    isReddit(content) ||
    isTweet(content) ||
    isYouTube(content)
  );
};

export const getNetwork = (content: ContentType) => {
  const { content_object } = content;
  if (content_object.url_metadata) {
    const { provider_name } = content_object.url_metadata;
    return provider_name;
  }

  return '';
};

export const isLinkedIn = (content: ContentType) => {
  return isHostMatch(content, HOST_MATCHERS.linkedin);
};

export const isFacebook = (content: ContentType) => {
  return isHostMatch(content, HOST_MATCHERS.facebook) && !isIgnoredFBPaths(content);
};

export const isIgnoredFBPaths = (content: ContentType) => {
  const { content_object } = content;

  if (!content_object.url) {
    return false;
  }

  const objectURL = new URL(content_object.url);
  const { pathname } = objectURL;
  const ignoreList = ['careers'];

  return includes(ignoreList, pathname.split('/')[1]);
};

export const isTwitter = (content: ContentType) => {
  const { content_object } = content;
  return !!content_object.tweet_metadata;
};

export const isInstagram = (content: ContentType) => {
  const { content_object } = content;
  return !!content_object.instagram_metadata;
};

export const isReddit = (content: ContentType) => {
  const { content_object } = content;
  return !!content_object.reddit_metadata;
};

export const isTweet = (content: ContentType) => {
  const { content_object } = content;
  return !!content_object.tweet_metadata;
};

export const isUrl = (content: ContentType) => {
  const { content_object } = content;
  return !!content_object.url_metadata;
};

export const isClientSideUrl = (content: ContentType) => {
  const { content_object } = content;
  return !!content_object.url;
};

export const isYouTube = (content: ContentType) => {
  const { content_object } = content;
  return !!content_object.youtube_metadata;
};

export const getInstagramShortcode = (content: ContentType) => {
  const { content_object } = content;
  const { instagram_metadata } = content_object;

  return instagram_metadata.shortcode;
};

export const getYouTubeId = (content: ContentType) => {
  const { content_object } = content;
  const { youtube_metadata } = content_object;

  return youtube_metadata.video_id;
};

export const getTweetId = (content: ContentType) => {
  const { content_object } = content;
  const { tweet_metadata } = content_object;

  return tweet_metadata.id_str;
};

export const getUrl = (content: ContentType) => {
  const { content_object } = content;
  return content_object.url;
};

export const getImage = (content: ContentType) => {
  if (!content) {
    return null;
  }

  const { content_object } = content;
  const { url_metadata, photos } = content_object;

  if (!isEmpty(photos)) {
    return photos[0];
  }

  if (isEmpty(url_metadata)) {
    return null;
  }

  const { images } = url_metadata;
  return images ? images[0] : null;
};

export const getPhoto = (content: ContentType) => {
  const { content_object } = content;
  const { photos } = content_object;

  if (!photos) {
    return null;
  }

  return photos ? photos[0] : null;
};

export const getVideo = (content: ContentType) => {
  const { content_object } = content;
  const { media } = content_object;

  if (!media || !media.video || !media.video) {
    return null;
  }

  return media.video;
};

export const getProviderDisplay = (content: ContentType) => {
  const { content_object } = content;
  const { url_metadata } = content_object;

  return url_metadata ? url_metadata.provider_display : '';
};

export const getProviderName = (content: ContentType) => {
  const { content_object } = content;
  const { url_metadata } = content_object;

  return url_metadata ? url_metadata.provider_name : '';
};

export const isPostCreator = (content: ContentType, user: UserProfile) => {
  if (!hasCreator(content)) {
    return false;
  }

  return content.user.id === user.id;
};

export const creatorIsAdmin = (content: ContentType) => {
  if (!content.user) {
    return false;
  }

  return content.user.user_type === UserType.Admin;
};

export const hasCreator = (content: ContentType) => {
  return !!content.user;
};

export const hasBody = (content: ContentType) => {
  const { content_object } = content;
  return !!content_object.body && content_object.body.length;
};

export const hasGroup = (content: ContentType) => {
  const { group_ids } = content;
  const groups = group_ids.filter((id: string) => id !== USER_FEED_ID);
  return !!groups.length;
};

export const hasUrl = (content: ContentType) => {
  const { content_object } = content;
  return !!content_object.url && content_object.url.length;
};

export const hasPhoto = (content: ContentType) => {
  return !!getPhoto(content);
};

export const hasVideo = (content: ContentType) => {
  return !!getVideo(content);
};

export const isTextOnly = (content: ContentType) => {
  return !getPhoto(content) && !isUrl(content);
};

export const isShareable = (content: ContentType) => {
  return content.is_shareable;
};

export const isEngageable = (content: ContentType) => {
  return content.is_engageable;
};

export const canAllowSharing = (content: ContentType) => {
  return isClientSideUrl(content) || isEmbed(content) || hasPhoto(content);
};

export const getGroupsByApproval = (content: ContentType, contentApproval: ContentApproval) => {
  const { groups, group_id_approvals } = content;

  return intersectionWith(
    groups,
    group_id_approvals,
    (group: Group, groupIdApproval: GroupIdApproval) => {
      if (group.id !== groupIdApproval.group_id) {
        return false;
      }

      const { approval } = groupIdApproval;
      return approval === contentApproval;
    },
  );
};

export const getApprovedGroups = (content: ContentType) => {
  const approvedGroups = getGroupsByApproval(content, ContentApproval.Approved);
  const autoApprovedGroups = getGroupsByApproval(content, ContentApproval.Autoapproved);

  return approvedGroups.concat(autoApprovedGroups);
};

export const getSubmittedGroups = (content: ContentType) => {
  return getGroupsByApproval(content, ContentApproval.Submitted);
};

export const getPendingGroups = (content: ContentType) => {
  return getGroupsByApproval(content, ContentApproval.Pending);
};

export const hasCommentary = (content: ContentType) => {
  if (!content.commentary) {
    return false;
  }

  return content.commentary.some((commentary: ContentCommentary) => !!commentary.commentary);
};

export const hasDisclosures = (content: ContentType) => {
  if (!content.disclosures) {
    return false;
  }

  return content.disclosures.some((disclosure: ContentDisclosures) => !!disclosure.disclosure_id);
};

export const isPinned = (content: ContentType) => {
  return content.is_pinned;
};

export const getVideoPoster = (content: ContentType) => {
  const video = getVideo(content);

  if (!video) {
    return null;
  }

  return video.poster;
};

export const getDisplayImage = (content: ContentType): DisplayImage => {
  if (hasVideo(content)) {
    return getVideoPoster(content);
  }

  if (hasPhoto(content)) {
    return getPhoto(content);
  }

  if (hasUrl(content)) {
    // @ts-ignore
    return getUrlImage(content);
  }

  return null;
};

export const getUrlImage = (content: ContentType) => {
  const { content_object } = content;
  const { url_metadata } = content_object;

  if (!url_metadata) {
    return null;
  }

  const { images } = url_metadata;
  return images ? images[0] : null;
};

export const getImageUrl = (content: ContentType): string => {
  const displayImage = getDisplayImage(content);

  if (!displayImage) {
    return null;
  }

  function isVideoPoster(image: DisplayImage): image is string {
    return typeof image === 'string';
  }

  function isS3Object(image: DisplayImage): image is S3Object {
    return (image as S3VideoObject | S3Object).key !== undefined;
  }

  // @ts-ignore
  function isUrlMetadata(image: DisplayImage): image is UrlMetadataImage {
    return (image as UrlMetadataImage).file_key !== undefined;
  }

  if (isVideoPoster(displayImage)) {
    return `${REACT_APP_IMGIX_HOST}/${displayImage}`;
  }

  const imageKey = isS3Object(displayImage) ? 'key' : 'file_key';

  if (displayImage[imageKey] && displayImage[imageKey].startsWith('file:')) {
    return displayImage[imageKey];
  }

  const params = queryString({
    auto: 'format,compress',
    w: displayImage.width,
    h: displayImage.height,
  });

  return `${REACT_APP_IMGIX_HOST}/${displayImage[imageKey]}?${params}`;
};

export const getTitle = (content: ContentType) => {
  const { content_object } = content;

  if (hasBody(content)) {
    return content_object.body;
  }

  if (hasUrl(content)) {
    return content_object.url_metadata?.title ?? '';
  }

  return '';
};
